/**
 * registerApexLanguage — registers the Apex language (Monarch tokenizer)
 * + the apex-light / apex-dark themes with Monaco. Two modes:
 * 1. On import: uses loader.init() (async, best-effort)
 * 2. Via registerApexWithInstance(monaco): sync, guaranteed — called from beforeMount
 *
 * Tokenizer rules live here (small, stable). Theme palettes live in
 * `apexMonacoThemes.ts` so they can be edited / extended independently.
 */
import { loader } from "@monaco-editor/react";

import {
  APEX_LIGHT_MONACO_THEME,
  APEX_DARK_MONACO_THEME,
  APEX_LIGHT_THEME_NAME,
  APEX_DARK_THEME_NAME,
} from "./apexMonacoThemes";

const APEX_LANG_DEF = {
  defaultToken: "",
  tokenPostfix: ".apex",
  keywords: [
    "abstract", "after", "before", "break", "catch", "class", "continue", "delete", "do",
    "else", "enum", "extends", "final", "finally", "for", "get", "global", "if", "implements",
    "import", "insert", "instanceof", "interface", "merge", "new", "null", "on", "override",
    "private", "protected", "public", "return", "set", "static", "super", "switch", "testmethod",
    "this", "throw", "trigger", "try", "undelete", "update", "upsert", "virtual", "void", "webservice",
    "when", "while", "with", "without", "sharing", "transient",
  ],
  typeKeywords: [
    "Boolean", "Blob", "Date", "Datetime", "Decimal", "Double", "Id", "Integer", "Long",
    "Object", "String", "Time", "List", "Map", "Set", "SObject", "Database", "System", "Test",
    "Limits", "Schema", "UserInfo", "ApexPages", "Messaging",
  ],
  operators: [
    "=", ">", "<", "!", "~", "?", ":", "==", "<=", ">=", "!=", "&&", "||",
    "++", "--", "+", "-", "*", "/", "&", "|", "^", "%", "<<", ">>", ">>>",
    "+=", "-=", "*=", "/=", "&=", "|=", "^=", "%=", "<<=", ">>=", ">>>=",
  ],
  symbols: /[=><!~?:&|+\-*/^%]+/,
  tokenizer: {
    root: [
      [/@(isTest|AuraEnabled|future|InvocableMethod|InvocableVariable|TestSetup|TestVisible|ReadOnly|RemoteAction|Deprecated)\b/, "annotation"],
      [/[a-zA-Z_$][\w$]*/, { cases: { "@keywords": "keyword", "@typeKeywords": "type", "@default": "identifier" } }],
      { include: "@whitespace" },
      [/[{}()[\]]/, "@brackets"],
      [/@symbols/, { cases: { "@operators": "operator", "@default": "" } }],
      [/\d*\.\d+([eE][-+]?\d+)?/, "number.float"],
      [/\d+/, "number"],
      [/'[^']*'/, "string"],
      [/"[^"]*"/, "string"],
    ],
    whitespace: [
      [/[ \t\r\n]+/, "white"],
      [/\/\*/, "comment", "@comment"],
      [/\/\/.*$/, "comment"],
    ],
    comment: [
      [/[^/*]+/, "comment"],
      [/\*\//, "comment", "@pop"],
      [/[/*]/, "comment"],
    ],
  },
} as any;

function _register(monaco: any): void {
  // Always (re-)define the themes — they're needed even if the language was
  // already registered (e.g. across hot reloads).
  try {
    monaco.editor.defineTheme(APEX_LIGHT_THEME_NAME, APEX_LIGHT_MONACO_THEME);
    monaco.editor.defineTheme(APEX_DARK_THEME_NAME, APEX_DARK_MONACO_THEME);
  } catch {
    /* */
  }
  if (monaco.languages.getLanguages().some((l: any) => l.id === "apex")) return;
  monaco.languages.register({ id: "apex", extensions: [".cls", ".trigger"], aliases: ["Apex", "apex"] });
  monaco.languages.setMonarchTokensProvider("apex", APEX_LANG_DEF);
}

/** Sync registration — call from beforeMount with the monaco instance */
export function registerApexWithInstance(monaco: any): void {
  _register(monaco);
}

/** Async registration — call on import via loader.init() */
export function registerApexLanguage(): void {
  loader.init().then(_register).catch(() => {});
}

// Auto-register on import
registerApexLanguage();
